import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Card, Spin, Table, Tabs, Tooltip } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import moment from 'moment';
import { clearNavbarState, updateNavbarState } from '../../actions/navbar_actions';
import { EmptyState } from '../empty_state/EmptyState';
import {
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from '../../utils/DataAccessUtils';
import { isThirdPartySupplier } from '../../utils/AuthUtils';
import { withUserType } from '../withUserType';
import {
	formatLeakRatePercent,
	formatPounds,
	getApplianceCategoryLabel,
	getProgrammeComplianceBand,
	getProgrammeThresholdPercent,
	getRefrigerantComplianceStatusLabel,
	getRefrigerantProgramme,
	getRefrigerantProgrammeStandardsLine,
	REFRIGERANT_PROGRAMME_ORDER,
	REFRIGERANT_PROGRAMMES,
	REFRIGERANT_SERVICING_RECORD_TC,
} from '../../utils/RefrigerantUtils';
import {
	refrigerantEventsRestCrudThunks,
	retrieveRefrigerantAssetSummary,
} from '../../thunks/refrigerant_events_thunks';
import { assetsRestCrudThunks } from '../../thunks/assets_thunks';
import getRefrigerantEventLedgerColumns from '../refrigerant/RefrigerantEventLedgerColumns';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { downloadServicingRecordCsv } from '../../utils/RefrigerantServicingRecordExport';
import './RefrigerantServicingRecordPage.less';

// The whole ledger, unpaged: a 608 record is a document, and a document with a page 2 the reader
// cannot see is not evidence of anything. Same bound as the appliance's own ledger tab.
const LEDGER_PAGE_SIZE = 200;

/**
 * The printable refrigerant servicing record for one appliance.
 *
 * A DOCUMENT, not a screen: everything on it is either a recorded fact about the equipment or a
 * figure the server derived from the append-only ledger, and nothing is editable. The one
 * client-side derivation is the "generated" date in the byline, which describes when the paper was
 * produced rather than anything about the appliance.
 *
 * The per-event certification is printed from each event's own SNAPSHOT (`certificationType` /
 * `certificationNumber`), never from the technician's current contact record. That is the whole
 * point of snapshotting it: a cert renewed or corrected next year must not silently rewrite what
 * last year's record attests to.
 *
 * The three programme tabs all render the SAME document against DIFFERENT standards, driven by
 * `REFRIGERANT_PROGRAMME_DEFINITIONS`. The appliance's facts and its service events do not change
 * between programmes; the threshold it is measured against, the resulting status, the standards line
 * and the footer all do. EPA 608's threshold and band come off the server's summary, because that is
 * what the server actually banded against; the voluntary and state programmes are derived on read
 * against their own numbers.
 */
const RefrigerantServicingRecordPage: FC<any> = ({
	asset,
	match,
	userType,
	currentUser,
	companyConfig,
	events,
	fetchAsset,
	fetchSummary,
	fetchEvents,
	updateNavbar,
	clearNavbar,
}) => {
	const assetId = nullSafeGet('params.id', match);
	const [summary, setSummary] = useState(null);
	const [loading, setLoading] = useState(true);
	// A printable 608 document must never be generated from a failed read: an empty record here does not
	// mean "no servicing took place", it means we could not find out.
	const [loadFailed, setLoadFailed] = useState(false);
	const [activeTab, setActiveTab] = useState(REFRIGERANT_PROGRAMMES.EPA_608);

	useEffect(() => {
		updateNavbar('Back to Asset', `/${userType}/assets/detail/${assetId}/refrigerantTracking`);
		return () => clearNavbar();
	}, [assetId, clearNavbar, updateNavbar, userType]);

	// The record on screen belongs to exactly one appliance. Navigating from one servicing record
	// straight to another must not leave the PREVIOUS appliance's document rendered under the new URL:
	// the only spinner path is `loading && !summary`, so a retained summary keeps a printable — and
	// downloadable — compliance document for the wrong asset live until the new requests land.
	const [summaryAssetId, setSummaryAssetId] = useState(assetId);
	if (summaryAssetId !== assetId) {
		setSummaryAssetId(assetId);
		setSummary(null);
		setLoadFailed(false);
	}

	// Bumped per refresh so a response that lost a race cannot overwrite the current appliance's record.
	const requestRef = useRef(0);

	// The asset the CURRENT request is for, readable from inside an older request's callback. A stale
	// `.then` closes over its own `assetId`, so it cannot otherwise know which appliance is on screen
	// by the time it runs.
	const assetIdRef = useRef(assetId);
	assetIdRef.current = assetId;

	// Which appliance the ledger currently in `REFRIGERANT_SERVICING_RECORD_TC` was fetched for.
	// Unlike the summary, the ledger is NOT component state: `fetchEvents` dispatches into a shared
	// redux collection, so a late response for appliance A overwrites the rows on screen for B no
	// matter what this component does with its own state. The guard below therefore has to do two
	// things a plain request-id check cannot — mark the ledger as belonging to another appliance so it
	// is not rendered, and re-issue the read so the shared collection converges back.
	//
	// A per-asset target collection would be the cleaner fix, but `getRecordsForTargetCollection`
	// throws on any name not declared up front in `refrigerant_events_reducer`, so dynamic names would
	// need that reducer reworked first.
	const [ledgerAssetId, setLedgerAssetId] = useState(null);

	useEffect(() => {
		const requestId = requestRef.current + 1;
		requestRef.current = requestId;
		setLoading(true);
		setLoadFailed(false);
		Promise.all([
			fetchAsset(assetId),
			fetchSummary(assetId).then((result) => {
				if (requestRef.current === requestId) setSummary(result);
			}),
			fetchEvents(assetId).then(() => {
				if (requestRef.current === requestId) {
					setLedgerAssetId(assetId);
					return;
				}
				// This response lost the race and has just overwritten the shared collection with a
				// PREVIOUS appliance's ledger. Pull the current one back in; `setLedgerAssetId` above
				// keeps the record hidden until it lands.
				setLedgerAssetId(assetId);
				fetchEvents(assetIdRef.current).then(() => setLedgerAssetId(assetIdRef.current));
			}),
		])
			// `.finally` does not consume a rejection. Without this catch a 401 or a network failure left an
			// unhandled rejection AND rendered the document with every figure as `--` and an empty event
			// table — a compliance record asserting that nothing was serviced, produced from reads that
			// never returned. Print and Download were both live on it.
			.catch(() => {
				if (requestRef.current === requestId) setLoadFailed(true);
			})
			.finally(() => {
				if (requestRef.current === requestId) setLoading(false);
			});
	}, [assetId, fetchAsset, fetchEvents, fetchSummary]);

	// Empty, not the collection's contents, while the rows in the shared collection belong to another
	// appliance. Rendering them would put appliance A's servicing history under appliance B's identity
	// block on a printable, downloadable compliance document — worse than rendering nothing, because
	// the reader has no way to tell. The `ledgerIsCurrent` gate below turns that into the spinner.
	const ledgerIsCurrent = ledgerAssetId === assetId;
	const ledger = useMemo(
		() =>
			ledgerIsCurrent ? getRecordsForTargetCollection(events, REFRIGERANT_SERVICING_RECORD_TC) : [],
		[events, ledgerIsCurrent]
	);

	const columns = useMemo(
		// Certification on, avatars off: this is printed. An avatar is a UI affordance that carries no
		// information onto paper, and the cert number is the field that makes the row evidence.
		() =>
			getRefrigerantEventLedgerColumns({
				userType,
				showAsset: false,
				showCertification: true,
				printable: true,
			}),
		[userType]
	);

	/**
	 * The record's identity block. `assetTags` first, `assetNumber` second: the design labels this
	 * "Asset Tag", and a site's own tag is what a tech reads off the equipment — the asset number is
	 * OpenWrench's identifier and only stands in when no tag was recorded.
	 */
	const assetTag =
		nullSafeGetOrElse('assetTags.0', asset, null) || nullSafeGet('assetNumber', asset) || '--';

	const locationLabel = useMemo(() => {
		const storeId = nullSafeGet('location.storeId', asset);
		const name = nullSafeGet('location.name', asset);
		if (!name) return '--';
		return storeId ? `Store #${storeId} - ${name}` : name;
	}, [asset]);

	const identityFields = useMemo(
		() => [
			{ label: 'Asset', value: nullSafeGetOrElse('name', asset, '--') },
			{ label: 'Asset Tag', value: assetTag },
			{ label: 'Location', value: locationLabel },
			{
				label: 'Category',
				value: nullSafeGet('applianceCategory', summary)
					? getApplianceCategoryLabel(nullSafeGet('applianceCategory', summary))
					: '--',
			},
			{ label: 'Refrigerant', value: nullSafeGetOrElse('refrigerantTypeCode', summary, '--') },
		],
		[asset, assetTag, locationLabel, summary]
	);

	const programme = useMemo(() => getRefrigerantProgramme(activeTab), [activeTab]);

	/**
	 * The threshold and band are resolved PER PROGRAMME, which is the whole point of the tabs: the
	 * same appliance sits at 20% under EPA 608 and 15% under GreenChill, so a record that printed one
	 * number under all three headings would be wrong on two of them.
	 */
	const programmeThresholdPercent = useMemo(
		() =>
			getProgrammeThresholdPercent(activeTab, nullSafeGet('applianceCategory', summary), summary),
		[activeTab, summary]
	);

	const programmeBand = useMemo(
		() => getProgrammeComplianceBand(activeTab, summary, programmeThresholdPercent),
		[activeTab, programmeThresholdPercent, summary]
	);

	const complianceFields = useMemo(
		() => [
			{ label: 'Full charge', value: formatPounds(nullSafeGetWithZero('fullChargeLb', summary)) },
			{
				label: '12-month leak rate',
				value: formatLeakRatePercent(nullSafeGetWithZero('leakRatePercent', summary)),
			},
			{
				label: programme.thresholdLabel,
				value:
					programmeThresholdPercent === null || programmeThresholdPercent === undefined
						? '--'
						: `${programmeThresholdPercent}%`,
			},
			{ label: 'Status', value: getRefrigerantComplianceStatusLabel(programmeBand) },
		],
		[programme, programmeBand, programmeThresholdPercent, summary]
	);

	const companyName = nullSafeGetOrElse(
		'buyerCompany.name',
		currentUser,
		nullSafeGetOrElse('name', companyConfig, '')
	);

	const handlePrint = useCallback(() => window.print(), []);

	const handleDownload = useCallback(
		() =>
			downloadServicingRecordCsv({
				asset,
				assetTag,
				locationLabel,
				summary,
				events: ledger,
				companyName,
				programmeKey: activeTab,
				thresholdPercent: programmeThresholdPercent,
				band: programmeBand,
			}),
		[
			activeTab,
			asset,
			assetTag,
			companyName,
			ledger,
			locationLabel,
			programmeBand,
			programmeThresholdPercent,
			summary,
		]
	);

	const renderRecord = () => (
		<Card className="refrigerant-servicing-record__sheet" bodyStyle={{ padding: 32 }}>
			<div className="flex flex-row flex-wrap items-start justify-between gap-4">
				<div className="flex flex-col">
					<span className="h3medium">Refrigerant servicing record</span>
					<span className="footnoteregular text-character-secondary">
						{[programme.name, `generated ${moment().format('MMM DD, YYYY')}`, companyName]
							.filter(Boolean)
							.join(' · ')}
					</span>
				</div>
				{/* Who produced the record. Present on the paper because "generated by" is part of what
				    makes a compliance document attributable. */}
				<div className="refrigerant-servicing-record__author">
					<ContactAvatar size="large" contact={currentUser} />
				</div>
			</div>

			<div className="refrigerant-servicing-record__facts">
				{identityFields.map((field) => (
					<div key={field.label} className="flex flex-col">
						<span className="footnoteregular text-character-secondary">{field.label}</span>
						<span className="bodymedium text-character-primary">{field.value}</span>
					</div>
				))}
			</div>

			{/* The programme's own scope and thresholds, so the reader can see what the status below was
			    measured against without having to know the programme by heart. */}
			<div className="refrigerant-servicing-record__standards footnoteregular text-character-secondary">
				{getRefrigerantProgrammeStandardsLine(activeTab)}
			</div>

			<div className="refrigerant-servicing-record__facts">
				{complianceFields.map((field) => (
					<div key={field.label} className="flex flex-col">
						<span className="footnoteregular text-character-secondary">{field.label}</span>
						<span className="bodymedium text-character-primary">{field.value}</span>
					</div>
				))}
			</div>

			<div className="h5medium" style={{ margin: '32px 0 8px' }}>
				Service Events
			</div>
			{ledger.length > 0 ? (
				<Table
					dataSource={ledger}
					columns={columns}
					rowKey={(record: any) => record.id}
					pagination={false}
					scroll={{ x: 'max-content' }}
				/>
			) : (
				<EmptyState
					graphic={null}
					headline={'No servicing recorded for this appliance yet.'}
					body={
						<div style={{ textAlign: 'center', maxWidth: 440 }}>
							A servicing record with no events is still a valid answer to an audit — it attests
							that nothing has been logged against this appliance.
						</div>
					}
				/>
			)}

			<div
				className="footnoteregular text-character-secondary"
				style={{ marginTop: 24, lineHeight: 1.6 }}
			>
				{programme.footer}
			</div>
		</Card>
	);

	// Matches the gate on the link that leads here (AssetRefrigerantTrackingPage). The link is hidden
	// for a third-party supplier, but the route is reachable by URL, and reads that fail closed onto an
	// empty ledger would render as a document attesting that no servicing took place.
	if (isThirdPartySupplier(currentUser)) {
		return (
			<EmptyState
				headline="Servicing record unavailable"
				body="The refrigerant servicing history for this appliance is held by the customer and is not shared with contractors."
			/>
		);
	}

	if (loadFailed) {
		return (
			<EmptyState
				headline="We could not load this servicing record"
				body="The refrigerant summary or the event ledger failed to load, so this record cannot be produced. Reload the page to try again."
			/>
		);
	}

	// `!ledgerIsCurrent` alongside the summary check: a stale ledger read that landed last leaves the
	// shared collection holding another appliance's rows, and the re-issued read is already in flight.
	// Spin until it lands rather than publish a record whose event table is empty for a reason the
	// reader cannot see.
	if ((loading && !summary) || !ledgerIsCurrent) {
		return (
			<div className="flex flex-row justify-center" style={{ padding: 48 }}>
				<Spin size="large" />
			</div>
		);
	}

	return (
		<div className="refrigerant-servicing-record flex flex-col p-4">
			<div className="refrigerant-servicing-record__toolbar flex flex-row items-center justify-between">
				<Tabs activeKey={activeTab} onChange={setActiveTab}>
					{REFRIGERANT_PROGRAMME_ORDER.map((programmeKey) => (
						<Tabs.TabPane tab={getRefrigerantProgramme(programmeKey).tabLabel} key={programmeKey} />
					))}
				</Tabs>
				<div className="flex flex-row items-center gap-2">
					<Tooltip title="Print this record">
						<Button icon={<PrinterOutlined />} onClick={handlePrint} />
					</Tooltip>
					<Tooltip title="Download the service events as CSV">
						<Button icon={<DownloadOutlined />} onClick={handleDownload} />
					</Tooltip>
				</div>
			</div>

			{renderRecord()}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	asset: getEntityById(ownProps.match.params.id, state.assets),
	events: state.refrigerant_events,
	currentUser: state.session.currentUser,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	fetchAsset: (assetId) => dispatch(assetsRestCrudThunks(ownProps.userType).readOne(assetId)),
	fetchSummary: (assetId) => dispatch(retrieveRefrigerantAssetSummary(ownProps.userType)(assetId)),
	fetchEvents: (assetId) =>
		dispatch(
			refrigerantEventsRestCrudThunks(ownProps.userType).read(
				{ assetId },
				REFRIGERANT_SERVICING_RECORD_TC,
				{ current: 1, pageSize: LEDGER_PAGE_SIZE },
				// Oldest first, unlike the working ledger. A servicing record is read as a narrative of
				// what happened to the appliance, and a narrative runs forwards.
				{ sort_by: 'occurredAt', order: 'ascend' }
			)
		),
	updateNavbar: (backlinkTitle, backlinkTo) =>
		dispatch(updateNavbarState(backlinkTitle, backlinkTo, null)),
	clearNavbar: () => dispatch(clearNavbarState()),
});

export default withUserType(
	withRouter<any, any>(
		connect(mapStateToProps, mapDispatchToProps)(RefrigerantServicingRecordPage as any)
	) as any
);
